import * as _ from 'lodash'
import util from './util';
import enums from './enum';
import serviceGateway from './serviceGateway';
import store from './store';
import * as globalActions from './actions';
import * as employeeActions from './employeeActions'; 
import jobModel from './models/jobModel';
import jobCostFactory, { IJobCost } from './models/jobCostModel';
import socketClient from './socketClient';
import { Cursor } from 'immutable/contrib/cursor';
import { Map } from 'immutable';
import { IModel } from './modelFactory';
import siteVisitFactory, { ISiteVisit } from './models/siteVisitModel';

function processJobResult(jobs, listResult = false) {
	const model = store().get('model') as Cursor;

	model.withMutations((mutable: Map<string, IModel>) => {
		const jobCosts: any[] = [];
		const siteVisits: any[] = [];

		_.forEach(jobs, j => {
			// map the jobCosts and siteVisits to an array of keys and store them until. . .
			j.jobCostKeys = _.map(j.jobCosts || [], jc => {
				jobCosts.push(jc);
				return jc.key;
			});

			j.siteVisitKeys = _.map(j.siteVisits || [], sv => {
				siteVisits.push(sv);
				return sv.key;
			});

			const jModel = jobModel(j);
			(jModel as any).isListShape = listResult;
			mutable.setIn(['jobs', j.key], jModel);
		});

		// . . . Now update or insert the jobCosts and siteVisits in the store
		normalizeChildren(mutable, listResult, jobCosts, 'jobCosts', jobCostFactory);
		normalizeChildren(mutable, listResult, siteVisits, 'siteVisits', siteVisitFactory);
		// _.forEach(jobCosts, jc => {
		// 	const existing = map.getIn(['jobCosts', jc.key]);

		// 	// only set partial jobCost if it doesn't exist or the itemVersion has changed
		// 	if (!listResult || !existing || existing.itemVersion < jc.itemVersion) {
		// 		map.setIn(['jobCosts', jc.key], jobCostModel(jc));
		// 	}
		// });
	});
}

function normalizeChildren(mutable: Map<string, IModel>, isPartialEntity: boolean, items: (IJobCost | ISiteVisit)[], storeKey, factory) {
	_.forEach(items, item => {
		const existing = mutable.getIn([storeKey, item.key]);

		// only set partial child if it doesn't exist or the itemVersion has changed
		if (!isPartialEntity || !existing || existing.itemVersion < item.itemVersion) {
			mutable.setIn([storeKey, item.key], factory(item));
		}
	});
}

export const socketClientHandlers = {
	['model.job.added']: () => {
		//fetchJob({ key: message.payload.key });
	},
	['model.job.updated']: message => {
		const keyPath = ['model', 'jobs', message.payload.key];
		const existing = store().getIn(keyPath);

		if (existing) {
			if (existing.itemVersion === message.payload.patchTargetVersion) {
				// TODO: handle possible jobCost patches
				existing.applyPatch(message.payload.patch);
				store().setIn(keyPath, existing);
			}
			else if (existing.itemVersion < message.payload.patchTargetVersion) {
				fetchJob({ key: message.payload.key });
			}
		}
	},
	['model.jobcost.updated']: message => {
		const keyPath = ['model', 'jobCosts', message.payload.key];
		const existing = store().getIn(keyPath);

		if (!existing && store().getIn(['viewState', 'currentModule']) === 'order-desk') {
			const statusLogPatch = _.find(message.payload.patch, op => op.path.indexOf('statusLog/') === 0 && op.op === 'add');
			if (statusLogPatch && statusLogPatch.value.status === 'pending-approval') {
				fetchJobsByCriteria({ jobCostStatuses: ['approved', 'pending-approval', 'rejected'] });
			}
		}
	}
};

// wrap handlers to allow extensions to override
for (const key of _.keys(socketClientHandlers)) {
	socketClient.on(key, message => {
		socketClientHandlers[key](message);
	});
}

// TODO: handle model.job.removed message

// NOTE: Action returns are only to allow tests to access the promise. DO NOT use return values in runtime code.
export const deleteJob = params => {
	const removedJobCosts: any[] = [];

	return serviceGateway.deleteJob(params)
		.then(() => {
			const model = store().get('model');

			model.withMutations(map => {
				const jobCosts = map.get('jobCosts').filter(jc => jc.jobKey === params.job.key);

				jobCosts.forEach(jc => {
					map = map.deleteIn(['jobCosts', jc.key]);
					removedJobCosts.push(jc);
				});

				map.deleteIn(['jobs', params.job.key]);
			});
		})
		.catch(err => {
			const model = store().get('model');

			model.withMutations(map => {
				_.forEach(removedJobCosts, jc => {
					map = map.setIn(['jobCosts', jc.key], jc);
				});

				map.setIn(['jobs', params.job.key], params.job);
			});

			globalActions.pushAlert({ type: 'warning', message: err.message || err, isCloseable: true });
		});
};

export const fetchJob = params => {
	return serviceGateway.fetchJob(params)
		.then(result => {
			if (result.job) {
				processJobResult([result.job]);
			}
		})
		.catch(err => {
			globalActions.pushAlert({ type: 'warning', message: err.message || err, isCloseable: true });
		});
};

export const fetchActiveJobs = params => {
	return serviceGateway.fetchActiveJobs(params)
		.then(result => {
			processJobResult(result.jobs, true);
		})
		.catch(err => {
			globalActions.pushAlert({ type: 'warning', message: err.message || err, isCloseable: true });
		});
};

export const fetchJobsByCriteria = params => {
	return serviceGateway.searchJobs(params)
		.then(result => {
			processJobResult(result.jobs, true);
		})
		.catch(err => {
			globalActions.pushAlert({ type: 'warning', message: err.message || err, isCloseable: true });
		});
};

export const searchJobs = params => {
	return serviceGateway.searchJobs(params)
		.then(result => {
			return _.map(result.jobs, j => {
				const resultModel = jobModel(j);
				(resultModel as any).jobCosts = _.map(j.jobCosts, jc => jobCostFactory(jc));
				return resultModel;
			});
		})
		.catch(err => {
			globalActions.pushAlert({ type: 'warning', message: err.message || err, isCloseable: true });
		});
};

export const fetchJobsForFYSalesStats = params => {
	const { fiscalYear } = params;
	const now = new Date();
	let startDate = fiscalYear.startDate;
	let endDate = fiscalYear.endDate;
	let searchStartDate = startDate;
	let searchEndDate = endDate;

	if (now.getTime() >= fiscalYear.startDate.getTime() && now.getTime() < fiscalYear.endDate.getTime()) {
		const monthDiff = util.dateHelper.diffMonths(now, fiscalYear.startDate);

		if (monthDiff > 8) {
			startDate = util.dateHelper.addMonths(startDate, 11 - monthDiff);
			endDate = searchEndDate = util.dateHelper.addYears(startDate, 1);
		}
		else if (monthDiff < 2) {
			searchStartDate = startDate = util.dateHelper.addMonths(startDate, monthDiff - 2);
		}
	}

	// let employees = getCollection({ collection: ModelCollection.employees });
	const employeePromise = employeeActions.fetchEmployees();

	return employeePromise
		.then(() => {
			return serviceGateway.searchJobStats({ businessSegment: params.businessSegment, startDate: searchStartDate, endDate: searchEndDate })
				.then(result => {
					processJobResult(result.jobs, true);

					const model = store().get('model');
					const employees = model.get('employees');
					const aggregates = {
						countLeads: 0,
						countProspects: 0,
						countCustomers: 0,
						sumContracted: 0,
						sumGoal: 0,
						sumProjected: 0,
						sumTotal: 0,
						accountManager: {}
					};

					_.forEach(result.jobs, j => {
						const job = model.getIn(['jobs', j.key]);
						const ballparkBudgetSetting = job.getSetting(enums.jobSettingGroups.Marketing, 'ballparkBudget');
						const projected = Number((ballparkBudgetSetting && ballparkBudgetSetting.value) || '0');
						const jobCosts = model.get('jobCosts').filter(jc => jc.jobKey === j.key).sortBy(jc => jc.dateCreated);
						// let contracted = 0;
						let hasContract = false;
						let firstEstimate;
						const employee = employees.get(job.accountManagerKey);
						let accountManagerAggregates = aggregates.accountManager[job.accountManagerKey];
						let countedAsMonthCustomer = false;

						if (!accountManagerAggregates) {
							const goal = Number((employee && employee.getProfileValue(`salesGoalFY${fiscalYear.year}`, '0')) || '0');

							accountManagerAggregates = aggregates.accountManager[job.accountManagerKey] = {
								employeeKey: job.accountManagerKey,
								name: employee && employee.fullName,
								sumGoal: goal,
								countLeads: 0,
								countProspects: 0,
								countCustomers: 0,
								sumProjected: 0,
								sumContracted: 0,
								sumTotal: 0
							};

							aggregates.sumGoal += goal;
						}

						jobCosts.forEach(jc => {
							// the first in dateCreated sort order
							firstEstimate = firstEstimate || jc;

							if (jc.isContract) {
								const status = jc.getMostRecentStatusByStatus('approved') || jc.getMostRecentStatusByStatus('pending-approval');

								if (status && status.dateCreated >= fiscalYear.startDate && status.dateCreated < fiscalYear.endDate) {
									const contractedKey = `contracted_${status.dateCreated.getFullYear().toString()}_${status.dateCreated.getMonth().toString()}`;
									const contractsKey = `contracts_${status.dateCreated.getFullYear().toString()}_${status.dateCreated.getMonth().toString()}`;
									const gtc = jc.getStatValue('grandTotalContract', undefined, 0);

									hasContract = true;

									// contracted += gtc;
									aggregates[contractedKey] = aggregates[contractedKey] || 0;
									aggregates[contractedKey] += gtc;

									aggregates.sumContracted += gtc;
									aggregates.sumTotal += gtc;

									accountManagerAggregates[contractedKey] = accountManagerAggregates[contractedKey] || 0;
									accountManagerAggregates.sumContracted += gtc;
									accountManagerAggregates.sumTotal += gtc;
									accountManagerAggregates[contractedKey] += gtc;

									if (!countedAsMonthCustomer) {
										aggregates[contractsKey] = aggregates[contractsKey] || 0;
										aggregates[contractsKey] += 1;

										accountManagerAggregates[contractsKey] = accountManagerAggregates[contractsKey] || 0;
										accountManagerAggregates[contractsKey] += 1;

										countedAsMonthCustomer = true;
									}
								}
							}
						});

						if (!firstEstimate) {
							const leadsKey = `leads_${job.dateCreated.getFullYear().toString()}_${job.dateCreated.getMonth().toString()}`;

							accountManagerAggregates.countLeads += 1;
							aggregates.countLeads += 1;

							aggregates[leadsKey] = aggregates[leadsKey] || 0;
							aggregates[leadsKey] += 1;

							accountManagerAggregates[leadsKey] = accountManagerAggregates[leadsKey] || 0;
							accountManagerAggregates[leadsKey] += 1;
						}
						else if (hasContract) {
							accountManagerAggregates.countCustomers += 1;
							aggregates.countCustomers += 1;
						}
						else {
							const prospectsKey = `prospects${firstEstimate.dateCreated.getFullYear().toString()}_${firstEstimate.dateCreated.getMonth().toString()}`;

							accountManagerAggregates.countProspects += 1;
							aggregates.countProspects += 1;

							aggregates[prospectsKey] = aggregates[prospectsKey] || 0;
							aggregates[prospectsKey] += 1;

							accountManagerAggregates[prospectsKey] = accountManagerAggregates[prospectsKey] || 0;
							accountManagerAggregates[prospectsKey] += 1;
						}

						if (!hasContract && ballparkBudgetSetting) {
							const projectedDate = new Date(job.getSettingValue(enums.jobSettingGroups.Marketing, 'projectedOrderDate', ballparkBudgetSetting.dateCreated.toISOString()));

							if (projectedDate >= fiscalYear.startDate && projectedDate < fiscalYear.endDate) {
								const projectedKey = `projected_${projectedDate.getFullYear().toString()}_${projectedDate.getMonth().toString()}`;

								aggregates[projectedKey] = aggregates[projectedKey] || 0;
								aggregates[projectedKey] += projected;
								aggregates.sumTotal += projected;

								accountManagerAggregates[projectedKey] = accountManagerAggregates[projectedKey] || 0;
								accountManagerAggregates[projectedKey] += projected;

								aggregates.sumProjected += projected;
								accountManagerAggregates.sumProjected += projected;
								accountManagerAggregates.sumTotal += projected;
							}
						}
					});

					globalActions.setSearchResults({ key: 'jobStats', fiscalYear: params.fiscalYear, startDate, endDate, timestamp: now.getTime(), aggregates });
				})
				.catch(err => {
					globalActions.pushAlert({ type: 'warning', message: err.message || err, isCloseable: true });
				});
		});
};

export const saveJob = params => {
	const keyPath = ['model', 'jobs', params.job.key];

	store().setIn(keyPath, jobModel(params.job));
	return serviceGateway.saveJob({ job: params.job, patch: params.job.dateCreated ? params.patch : undefined })
		.then(result => {
			processJobResult([result.job]);
		})
		.catch(err => {
			const key = util.generateGuid();
			const retry = p => {
				globalActions.clearAlert({ key: key });
				return saveJob(p || params);
			};

			globalActions.pushAlert({
				key: key,
				type: 'error',
				message: err.message || 'Server Error',
				source: 'server',
				action: 'saveJob',
				retry: retry,
				params: params,
				orgininalError: err,
				isCloseable: true
			});
		});
};